import { createStore } from 'zustand';

/** A single file or image pending attachment to the next workbench message. */
export interface Attachment {
	/** Stable ID for keyed rendering and removal. */
	id: string;
	/** Original file name. */
	name: string;
	/** MIME type, e.g. `"image/jpeg"` or `"application/pdf"`. */
	mediaType: string;
	/** Base64-encoded content without the `data:` prefix. */
	data: string;
	/** Full `data:` URI used to render image thumbnails. */
	previewUrl: string;
}

/** Max file size accepted for attachment (10 MB). */
export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

/** MIME types accepted as image attachments. */
export const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

/** MIME types accepted as document attachments. */
export const ACCEPTED_DOCUMENT_TYPES = new Set(['application/pdf']);

/** Maximum number of sent-attachment entries retained to bound memory usage. */
const SENT_MAP_MAX_SIZE = 50;

/** State and actions for one workbench pane's attachment store. */
export interface AttachmentStoreState {
	/** Attachments queued for the next message send, in insertion order. */
	pending: readonly Attachment[];
	/** Attachments already sent, keyed by the assistant-ui message ID they were sent with. */
	sent: ReadonlyMap<string, readonly Attachment[]>;
	/** Adds an attachment to the pending queue. */
	add: (attachment: Attachment) => void;
	/** Removes a pending attachment by ID; no-op for unknown IDs. */
	remove: (id: string) => void;
	/** Returns the pending queue and clears it atomically. */
	consume: () => readonly Attachment[];
	/**
	 * Associates a set of sent attachments with an assistant-ui message ID.
	 * No-op for empty sets. Evicts the oldest entry once the retention cap is
	 * exceeded to bound memory.
	 */
	storeSent: (messageId: string, attachments: readonly Attachment[]) => void;
}

/**
 * Creates an attachment store scoped to a single workbench pane. Instances are
 * provided through `AttachmentStoreProvider` so that multiple workbench panes on
 * screen at once each keep their own pending and sent attachments.
 *
 * @returns A vanilla Zustand store holding {@link AttachmentStoreState}.
 */
export function createAttachmentStore() {
	return createStore<AttachmentStoreState>()((set, get) => ({
		pending: [],
		sent: new Map(),

		add: (attachment) => set((state) => ({ pending: [...state.pending, attachment] })),

		remove: (id) => set((state) => ({ pending: state.pending.filter((a) => a.id !== id) })),

		consume: () => {
			const current = get().pending;
			if (current.length > 0) {
				set({ pending: [] });
			}
			return current;
		},

		storeSent: (messageId, attachments) => {
			if (attachments.length === 0) {
				return;
			}
			set((state) => {
				const sent = new Map(state.sent);
				sent.set(messageId, attachments);
				// Evict the oldest entry once the cap is exceeded to prevent unbounded
				// memory growth from accumulating base64 payloads over long sessions.
				if (sent.size > SENT_MAP_MAX_SIZE) {
					const oldest = sent.keys().next().value;
					if (oldest !== undefined) {
						sent.delete(oldest);
					}
				}
				return { sent };
			});
		},
	}));
}

/** Handle to a per-pane attachment store created by {@link createAttachmentStore}. */
export type AttachmentStoreApi = ReturnType<typeof createAttachmentStore>;
